import h from 'hyperscript';

import { HEADER_DISY_CANCEL_ID, HEADER_REQUESTED_WITH } from '@disy/lib.backend/api-constants';
import type { BackendAbortSignal } from '@disy/lib.backend/backend-abort-controller';
import { on } from '@disy/lib/event-util';
import { getLogger } from '@disy/lib/logging';

import { defaultXhrErrorHandler } from 'cadenza/api-client/api';

const LOGGER = getLogger('cadenza/utils/download');

// INFO level (also in production), because blob downloads
// do not show up in the devtools' network tab.
LOGGER.setLevel(LOGGER.levels.INFO);

/**
 * Loads a file via XHR from the given URL and using the given options.
 * The file is downloaded entirely as a blob before the browser's download action is triggered
 * via an invisible anchor.
 *
 * Use this function if you need to...
 * - set request headers (e.g. Accept) or change the request method (e.g. to HEAD).
 * - know when the download is finished or whether it was successful.
 * - specify the download filename.
 *
 * Otherwise use {@link downloadImmediately}.
 *
 * Example usage:
 *
 * download(url);
 * download(url, {
 *   headers: {
 *     'Accept': 'application/pdf', // What we'd like to get.
 *     'Authorization': 'Your auth key'
 *   },
 *   filename: 'Over'
 * });
 *
 * @param {string} url - The URL of the file to download
 * @param {object} options - Options for the XHR request
 * @return {promise} a Promise that resolves when the file is downloaded
 */
export function download(
  url: string,
  {
    abortSignal,
    filename,
    headers = {},
  }: {
    abortSignal?: AbortSignal | BackendAbortSignal;
    headers?: Record<string, string>;
    filename?: string;
  } = {},
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (abortSignal) {
      on(abortSignal, 'abort', () => xhr.abort(), { once: true });

      if ('id' in abortSignal) {
        headers[HEADER_DISY_CANCEL_ID] = abortSignal.id;
      }
    }

    xhr.open('GET', url, true);
    // required for XHR check on server
    xhr.setRequestHeader(HEADER_REQUESTED_WITH, 'XMLHttpRequest');
    Object.entries(headers).forEach(([header, value]) => xhr.setRequestHeader(header, value));
    xhr.responseType = 'blob';

    xhr.onload = function () {
      if (xhr.status === 200) {
        filename ??= getFilename(xhr) ?? url.split('/').pop();
        LOGGER.info(`Downloading "${filename}" as a blob …`);
        downloadObject(xhr.response, filename);
        resolve(filename);
      } else {
        defaultXhrErrorHandler(xhr, reject);
      }
    };

    xhr.send();
  });
}

// https://stackoverflow.com/a/40940790
function getFilename(xhr: XMLHttpRequest) {
  return xhr
    .getResponseHeader('Content-Disposition')
    ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
    ?.replace(/['"]/g, '');
}

/**
 * Allows user to download from an object (i.e. Blob, File or MediaSource).
 * Takes care of revoking the object URL to avoid memory leaks.
 * @param {Blob | File | MediaSource} obj The object to download
 * @param {string} [fileName] Default file name to be proposed to the user
 */
export function downloadObject(obj: Blob, fileName?: string) {
  const objectUrl = URL.createObjectURL(obj);
  try {
    downloadImmediately(objectUrl, fileName);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Downloads the file from the given URL, without previously loading the whole file in a Blob.
 *
 * This allows the user to see the download progress via the browser's UI.
 *
 * It might be preferable for big files since the user can manage the download via the browser's UI.
 * It provides a more consistent and familiar UI.
 *
 * It's also recommended for client-side generated content like object URLs and data URLs.
 *
 * However, it allows much less control over the HTTP request than the other download function.
 *
 * @param {string} url - The URL of the file to download
 * @param {string} [fileName] Default file name to be proposed to the user
 */
export function downloadImmediately(url: string, fileName = '') {
  // create and attach a hidden anchor for the URL
  const a = h('a', {
    href: url,
    // this attribute makes sure the content is downloaded and no navigation occurs to the URL
    // and sets the default file name proposed in the save dialog
    download: fileName,
    hidden: true,
  });
  document.body.appendChild(a);

  // trigger the download
  a.click();

  // remove the anchor
  a.remove();
}
